import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ReadinessStatusChip,
  readinessStatusLabels,
  UNSUPPORTED_BLOCKER_LABEL,
  type ReadinessChipValue,
  type ReadinessItem,
  type ReadinessStatus,
} from '../../employee-readiness';
import type { EmployeeSetupReadiness } from '../../employee-readiness';
import { destinationPath } from '../../employee-readiness/destinationPath';
import { employmentStatusLabels, type EmployeeResponse } from '../../people/types';
import { employeesFailureCopy, readinessFailureCopy } from '../safeErrors';
import { usePayEmployeesData, type ReadinessLoad } from '../usePayEmployeesData';
import { WriteCheckFlow } from '../../check-batch/WriteCheckFlow';
import '../pay-employees.css';

/**
 * EPN-71 Phase 1 — Employee Selection & Readiness.
 *
 * Deliberately NOT a payroll-processing screen. It reads the employee list and the authoritative EPN-94
 * readiness projection, lets the user select employees, and enables "Continue" only when every selected
 * employee is server-reported `Ready` or `ReadyWithWarnings` (EPN-94: "payroll can proceed, but some items need
 * review first"). `Blocked`, `NeedsReview` and `NotApplicable` never proceed. A `ReadyWithWarnings` employee's
 * warnings stay visible — in the table, beside Continue and on the next state — and are never folded into Ready.
 * "Continue" advances to an informational state; it creates no pay run, runs no calculation, and approves/pays
 * nothing. See docs/epn-71-pay-employees.md for the phase plan.
 */

/** The only statuses that may proceed. Everything else — including a status this code doesn't know — cannot. */
const PROCEEDING_STATUSES: ReadonlySet<ReadinessStatus> = new Set<ReadinessStatus>(['Ready', 'ReadyWithWarnings']);

/** Only an Active, non-archived employee can be selected. Selecting a non-Active employee would need an
 * explicit acknowledgment rule that has not been approved, so such rows are shown but not selectable. */
function isSelectable(employee: EmployeeResponse): boolean {
  return employee.status === 0 && employee.isActive;
}

function statusLabel(employee: EmployeeResponse): string {
  if (employee.status === 0 && !employee.isActive) return 'Archived';
  return employmentStatusLabels[employee.status] ?? 'Unknown';
}

function chipFor(readiness: ReadinessLoad, employeeId: string): ReadinessChipValue {
  switch (readiness.kind) {
    case 'loading': return 'loading';
    case 'error': return readiness.reason === 'forbidden' ? 'forbidden' : 'unavailable';
    case 'loaded': return readiness.byId[employeeId]?.status ?? 'unavailable';
  }
}

function stateText(item: ReadinessItem): string {
  switch (item.state) {
    case 'Missing': return 'Missing';
    case 'Unverifiable': return 'Could not be verified';
    case 'CapabilityUnavailable': return UNSUPPORTED_BLOCKER_LABEL;
    case 'Complete': return 'Complete';
    case 'NotApplicable': return 'Not applicable';
  }
}

const OUTSTANDING_STATES: ReadonlySet<ReadinessItem['state']> = new Set(['Missing', 'CapabilityUnavailable', 'Unverifiable']);

/** Items the server says keep this employee from proceeding — exactly as returned, nothing re-derived and
 * nothing downgraded (a `CapabilityUnavailable` item stays a blocker). */
function blockersOf(readiness: EmployeeSetupReadiness): ReadinessItem[] {
  return readiness.items.filter((i) => i.severity === 'Blocker' && OUTSTANDING_STATES.has(i.state));
}

/** Items the server flags as needing review but not blocking (EPN-94 `Warning` severity). */
function warningsOf(readiness: EmployeeSetupReadiness): ReadinessItem[] {
  return readiness.items.filter((i) => i.severity === 'Warning' && OUTSTANDING_STATES.has(i.state));
}

function ItemList({ employee, items }: { employee: EmployeeResponse; items: ReadinessItem[] }) {
  const name = `${employee.firstName} ${employee.lastName}`;
  return (
    <ul className="readiness-list">
      {items.map((item) => {
        const path = destinationPath(employee.id, item);
        return (
          <li key={item.key} className={`readiness-item readiness-item--${item.state}`} data-item-key={item.key}>
            <div className="readiness-item-main">
              <strong>{item.label}</strong>
              <span className="readiness-item-state">{stateText(item)}</span>
              <p className="muted readiness-item-message">{item.message}</p>
            </div>
            {path && (
              <Link className="link readiness-item-link" to={path} aria-label={`View section for ${item.label} (${name})`}>
                View section
              </Link>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** Blockers and warnings for one employee. Warnings are never collapsed away: a `ReadyWithWarnings` row opens
 * with its warnings showing, because Continue is enabled for it and the user must see what needs review. */
function ReadinessDetails({ employee, readiness }: { employee: EmployeeResponse; readiness: EmployeeSetupReadiness }) {
  const blockers = blockersOf(readiness);
  const warnings = warningsOf(readiness);
  const name = `${employee.firstName} ${employee.lastName}`;
  if (readiness.status === 'Ready') return null;

  const parts = [
    blockers.length > 0 ? plural(blockers.length, 'blocker', 'blockers') : null,
    warnings.length > 0 ? plural(warnings.length, 'warning', 'warnings') : null,
  ].filter((p): p is string => p !== null);

  return (
    <details
      className="pay-employees-blockers"
      data-testid={`blockers-${employee.id}`}
      open={readiness.status === 'ReadyWithWarnings'}
    >
      <summary aria-label={`Readiness details for ${name}`}>{parts.length > 0 ? parts.join(', ') : 'Details'}</summary>
      {parts.length === 0 && <p className="muted">The readiness check did not return item-level detail for this employee.</p>}
      {blockers.length > 0 && (
        <>
          <h5 className="pay-employees-detail-heading">Blockers</h5>
          <ItemList employee={employee} items={blockers} />
        </>
      )}
      {warnings.length > 0 && (
        <>
          <h5 className="pay-employees-detail-heading">Warnings — review before payroll</h5>
          <ItemList employee={employee} items={warnings} />
        </>
      )}
    </details>
  );
}

interface Gate {
  allowed: boolean;
  reason: string | null;
  /** Selected employees that may proceed but carry warnings — shown beside Continue and on the next state. */
  warned: { employee: EmployeeResponse; items: ReadinessItem[] }[];
}

function continueGate(selected: EmployeeResponse[], readiness: ReadinessLoad): Gate {
  const none: Gate['warned'] = [];
  if (selected.length === 0) return { allowed: false, reason: 'Select at least one employee to continue.', warned: none };
  if (readiness.kind === 'loading') return { allowed: false, reason: 'Payroll readiness is still being checked.', warned: none };
  if (readiness.kind === 'error') {
    return { allowed: false, reason: 'Payroll can’t continue until readiness is confirmed for every selected employee.', warned: none };
  }

  const stopped = selected.filter((e) => {
    const status = readiness.byId[e.id]?.status;
    return status === undefined || !PROCEEDING_STATUSES.has(status);
  });
  if (stopped.length > 0) {
    const described = stopped.map((e) => {
      const status = readiness.byId[e.id]?.status;
      return `${e.firstName} ${e.lastName} — ${status ? readinessStatusLabels[status] : 'readiness unavailable'}`;
    });
    return {
      allowed: false,
      reason: `${plural(stopped.length, 'selected employee is', 'selected employees are')} not payroll-ready (${described.join('; ')}). Deselect them or resolve what’s stopping them to continue.`,
      warned: none,
    };
  }

  const warned = selected.flatMap((e) => {
    const result = readiness.byId[e.id];
    return result?.status === 'ReadyWithWarnings' ? [{ employee: e, items: warningsOf(result) }] : [];
  });
  return { allowed: true, reason: null, warned };
}

function WarningSummary({ warned }: { warned: Gate['warned'] }) {
  if (warned.length === 0) return null;
  return (
    <div className="pay-employees-warning-summary" role="status" data-testid="selected-warnings">
      <strong>
        {plural(warned.length, 'selected employee has', 'selected employees have')} warnings that need review first.
      </strong>
      <ul>
        {warned.map(({ employee, items }) => (
          <li key={employee.id}>
            {employee.firstName} {employee.lastName}
            {items.length > 0 ? `: ${items.map((i) => i.label).join(', ')}` : ''}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** EPN-231 — the payment-method tabs. Write Check is the four-step check batch; Direct Deposit keeps the EPN-71 Phase 1 employee selection
 * unchanged (no direct-deposit batch exists yet). The method lives in the URL (`?method=check`) so a link or a reload lands on the same tab. */
function MethodTabs({ method, onSelect }: { method: 'check' | 'directDeposit'; onSelect: (m: 'check' | 'directDeposit') => void }) {
  return (
    <div className="wc-tabs" role="tablist" aria-label="Payment method">
      <button type="button" role="tab" id="pay-method-tab-dd" aria-selected={method === 'directDeposit'} className="wc-tab" onClick={() => onSelect('directDeposit')}>
        Direct Deposit
      </button>
      <button type="button" role="tab" id="pay-method-tab-check" aria-selected={method === 'check'} className="wc-tab" onClick={() => onSelect('check')}>
        Write Check
      </button>
    </div>
  );
}

export function PayEmployeesPage() {
  const [params, setParams] = useSearchParams();
  const method: 'check' | 'directDeposit' = params.get('method') === 'check' ? 'check' : 'directDeposit';
  const tabs = <MethodTabs method={method} onSelect={(m) => setParams(m === 'check' ? { method: 'check' } : {}, { replace: true })} />;
  return method === 'check' ? (
    <WriteCheckFlow tabs={tabs} />
  ) : (
    <>
      <div className="pay-employees-tabs">{tabs}</div>
      <DirectDepositSelection />
    </>
  );
}

function DirectDepositSelection() {
  const { employees, readiness, retryEmployees, recheckReadiness } = usePayEmployeesData();
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  const [stage, setStage] = useState<'select' | 'ready'>('select');

  const employeeList = useMemo(() => (employees.kind === 'loaded' ? employees.employees : []), [employees]);
  // Selection is always intersected with what is currently selectable, so a stale id can never count.
  const selected = useMemo(() => employeeList.filter((e) => selectedIds.has(e.id) && isSelectable(e)), [employeeList, selectedIds]);
  const selectable = useMemo(() => employeeList.filter(isSelectable), [employeeList]);
  const gate = continueGate(selected, readiness);

  function toggle(employee: EmployeeResponse) {
    if (!isSelectable(employee)) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(employee.id)) next.delete(employee.id);
      else next.add(employee.id);
      return next;
    });
  }

  const allSelectableSelected = selectable.length > 0 && selectable.every((e) => selectedIds.has(e.id));
  function toggleAll() {
    setSelectedIds(allSelectableSelected ? new Set() : new Set(selectable.map((e) => e.id)));
  }

  if (stage === 'ready') {
    return (
      <section className="card card--wide">
        <div className="section-header">
          <h2>Pay Employees</h2>
        </div>
        <h3>Payroll setup ready</h3>
        <p>Employee selection and readiness are complete.</p>
        <p className="muted">
          Payroll run creation is part of the next EPN-71 phase. Nothing has been created, calculated, approved or paid, and
          this selection has not been saved.
        </p>
        <WarningSummary warned={gate.warned} />
        <h4>Selected employees ({selected.length})</h4>
        <ul>
          {selected.map((e) => (
            <li key={e.id}>
              {e.firstName} {e.lastName} {e.employeeNumber && <span className="muted">· {e.employeeNumber}</span>}
            </li>
          ))}
        </ul>
        <div className="section-header">
          <button type="button" className="link" onClick={() => setStage('select')}>
            Back to employee selection
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="card card--wide pay-employees">
      <div className="section-header">
        <h2>Pay Employees</h2>
      </div>
      <p>
        Choose the employees you want to include in payroll. Every selected employee must pass payroll readiness before payroll can
        continue.
      </p>
      <p className="muted">
        Readiness is calculated by EPN from each employee’s setup records and can’t be changed on this screen. This screen doesn’t run,
        calculate, approve or pay payroll.
      </p>

      {employees.kind === 'loading' && <p className="muted" role="status">Loading employees…</p>}

      {employees.kind === 'error' && (
        <div role="alert">
          <p className="error">{employeesFailureCopy(employees.reason)}</p>
          {employees.reason !== 'forbidden' && (
            <button type="button" onClick={retryEmployees}>Try again</button>
          )}
        </div>
      )}

      {employees.kind === 'loaded' && employeeList.length === 0 && (
        <p className="muted">No employees found. Add employees under People before running payroll.</p>
      )}

      {employees.kind === 'loaded' && employeeList.length > 0 && (
        <>
          {readiness.kind === 'loading' && <p className="muted" role="status">Checking payroll readiness…</p>}
          {readiness.kind === 'error' && (
            <div role="alert">
              <p className="error">{readinessFailureCopy(readiness.reason)}</p>
              {readiness.reason !== 'forbidden' && (
                <button type="button" onClick={recheckReadiness}>Try again</button>
              )}
            </div>
          )}

          <p className="muted" data-testid="employees-selected-count">
            Employees selected: <strong>{selected.length}</strong>
          </p>

          <div className="table-scroll" role="region" aria-label="Employees and payroll readiness" tabIndex={0}>
            <table aria-label="Employees and payroll readiness" className="pay-employees-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      aria-label="Select all eligible employees"
                      checked={allSelectableSelected}
                      disabled={selectable.length === 0}
                      onChange={toggleAll}
                    />
                  </th>
                  <th>Employee</th>
                  <th>Employee #</th>
                  <th>Status</th>
                  <th>Payroll readiness</th>
                </tr>
              </thead>
              <tbody>
                {employeeList.map((e) => {
                  const selectableRow = isSelectable(e);
                  const result = readiness.kind === 'loaded' ? readiness.byId[e.id] : undefined;
                  return (
                    <tr key={e.id} data-testid={`employee-row-${e.id}`}>
                      <td>
                        <input
                          type="checkbox"
                          aria-label={`Include ${e.firstName} ${e.lastName}`}
                          checked={selectableRow && selectedIds.has(e.id)}
                          disabled={!selectableRow}
                          aria-describedby={selectableRow ? undefined : `not-selectable-${e.id}`}
                          onChange={() => toggle(e)}
                        />
                      </td>
                      <td>
                        {e.firstName} {e.lastName}
                      </td>
                      <td>{e.employeeNumber}</td>
                      <td>
                        {statusLabel(e)}
                        {!selectableRow && (
                          <span id={`not-selectable-${e.id}`} className="muted pay-employees-not-selectable">
                            {' '}— can’t be selected: only Active employees can be paid from this screen.
                          </span>
                        )}
                      </td>
                      <td>
                        <ReadinessStatusChip value={chipFor(readiness, e.id)} />
                        {result && <ReadinessDetails employee={e} readiness={result} />}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="section-header">
            <button type="button" className="link" onClick={recheckReadiness} disabled={readiness.kind === 'loading'}>
              Re-check readiness
            </button>
            <button type="button" disabled={!gate.allowed} aria-describedby="pay-employees-continue-reason" onClick={() => setStage('ready')}>
              Continue
            </button>
          </div>
          <WarningSummary warned={gate.warned} />
          <p id="pay-employees-continue-reason" className="muted" role="status">
            {gate.reason}
          </p>
        </>
      )}
    </section>
  );
}
